'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useIsClient } from '@/hooks';
import ProblemHighlightGridRedesign from './variants/Redesign';

/**
 * Props for `ProblemHighlightGrid`.
 */
export type ProblemHighlightGridProps =
  SliceComponentProps<Content.ProblemHighlightGridSlice>;

const ProblemHighlightGrid: FC<ProblemHighlightGridProps> = ({
  slice,
  ...rest
}) => {
  if (slice.variation === 'redesign') {
    return <ProblemHighlightGridRedesign slice={slice} {...rest} />;
  }
  return <ProblemHighlightGridCurrent slice={slice} {...rest} />;
};

/**
 * Component for "ProblemHighlightGrid" Slices.
 * Displays a headline and subheadline, followed by a grid layout with four
 * highlighted problem/statistic items. Each item includes an icon, title,
 * and supporting text.
 */
const ProblemHighlightGridCurrent: FC<ProblemHighlightGridProps> = ({
  slice,
}) => {
  // `slice.primary` is a union across variations; `problems` and `main_icon`
  // only exist on the stat/dark variations (the centered ones read their own
  // fields via casts below). Narrow to that shape for the shared fields.
  const { title, subtitle, problems, main_icon } =
    slice.primary as Content.ProblemHighlightGridSliceHeadlineFourStatsPrimary;
  const isDarkSided = slice.variation === 'darkSided';
  const isDarkNormal = slice.variation === 'darkNormal';
  const isCenteredStatements =
    (slice.variation as string) === 'centeredStatements';
  const isCenteredCards = (slice.variation as string) === 'centeredCards';

  const kicker = (slice.primary as unknown as { kicker?: string }).kicker;
  const statements = (
    slice.primary as unknown as { statements?: { statement?: string }[] }
  ).statements;
  const intro = (
    slice.primary as unknown as {
      intro?: import('@prismicio/client').RichTextField;
    }
  ).intro;
  const secondaryTitle = (
    slice.primary as unknown as {
      secondary_title?: import('@prismicio/client').RichTextField;
    }
  ).secondary_title;
  const secondaryDescription = (
    slice.primary as unknown as {
      secondary_description?: import('@prismicio/client').RichTextField;
    }
  ).secondary_description;
  const cards = (
    slice.primary as unknown as {
      cards?: { card_title?: string; card_description?: string }[];
    }
  ).cards;
  const closing = (slice.primary as unknown as { closing?: string }).closing;
  const removeUpperMargin = (
    slice.primary as unknown as { remove_upper_margin?: boolean }
  ).remove_upper_margin;

  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const headerEl = headerRef.current;
    const cardsEl = cardsRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (cardsEl) {
      cardsEl.style.transitionDelay = '200ms';
      observer.observe(cardsEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (cardsEl) {
        observer.unobserve(cardsEl);
      }
    };
  }, [isClient]);

  // Centered, light layout: kicker pill, headline, stacked pill statements,
  // supporting paragraphs and a colored closing line.
  if (isCenteredStatements) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className={`bg-[#FAFAFA] px-5 md:px-8 pb-20 md:pb-28 ${
          removeUpperMargin ? 'pt-0' : 'pt-20 md:pt-28'
        }`}
      >
        <div className="max-w-[1140px] mx-auto flex flex-col items-center gap-8 md:gap-10 text-center">
          {/* Header */}
          <div
            ref={headerRef}
            className="flex w-full flex-col items-center gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {kicker && (
              <span className="rounded-full border border-[#7c5cff] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#7c5cff] md:text-[12px]">
                {kicker}
              </span>
            )}

            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="w-full text-balance break-words text-[28px] md:text-[42px] font-semibold leading-[1.2] text-[#041018]">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
          </div>

          {/* Intro / lead text above the cards */}
          {isFilled.richText(intro) && (
            <div className="w-full max-w-[720px]">
              <PrismicRichText
                field={intro}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-pretty text-[14px] font-normal leading-[1.6] text-[#041018]/70 md:text-[15px] [&:not(:first-child)]:mt-2">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#041018]">
                      {children}
                    </strong>
                  ),
                }}
              />
            </div>
          )}

          {/* Symptom statements — grounded cards with a purple accent + left
              alignment so they read as a connected list, not floating pills. */}
          {statements && statements.length > 0 && (
            <div
              ref={cardsRef}
              className="flex w-full max-w-[720px] flex-col gap-3 opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {statements.map((item, index) =>
                item.statement ? (
                  <div
                    key={index}
                    className="flex items-center gap-4 rounded-2xl border border-[#041018]/10 bg-white px-6 py-4 text-left shadow-[0_1px_3px_rgba(4,16,24,0.04)]"
                  >
                    <span
                      aria-hidden
                      className="h-5 w-[3px] shrink-0 rounded-full bg-[#7c5cff]"
                    />
                    <span className="text-[14px] font-medium leading-[1.4] text-[#041018] md:text-[15px]">
                      {item.statement}
                    </span>
                  </div>
                ) : null,
              )}
            </div>
          )}

          {/* Supporting paragraphs */}
          {isFilled.richText(subtitle) && (
            <div className="w-full max-w-[720px]">
              <PrismicRichText
                field={subtitle}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-pretty text-[14px] font-normal leading-[1.6] text-[#041018]/70 md:text-[15px] [&:not(:first-child)]:mt-4">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#041018]">
                      {children}
                    </strong>
                  ),
                  hyperlink: ({ children, node }) => (
                    <a
                      href={node.data.url ?? '#'}
                      className="text-[#4b0fea] underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              />
            </div>
          )}

          {/* Secondary title + description */}
          {isFilled.richText(secondaryTitle) && (
            <PrismicRichText
              field={secondaryTitle}
              components={{
                heading2: ({ children }) => (
                  <h2 className="mt-2 text-balance text-[24px] md:text-[36px] font-semibold leading-[1.2] text-[#041018]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(secondaryDescription) && (
            <div className="w-full max-w-[720px]">
              <PrismicRichText
                field={secondaryDescription}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-pretty text-[14px] font-normal leading-[1.6] text-[#041018]/70 md:text-[15px] [&:not(:first-child)]:mt-4">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#041018]">
                      {children}
                    </strong>
                  ),
                }}
              />
            </div>
          )}

          {/* Closing resolution line — anchored with a divider so it lands as
              the section's conclusion instead of floating. */}
          {closing && (
            <div className="mt-2 w-full max-w-[720px] border-t border-[#041018]/10 pt-8">
              <p className="text-balance text-[18px] font-bold leading-[1.4] text-[#4b0fea] md:text-[20px]">
                {closing}
              </p>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Centered, light layout with a row of problem cards (title + description),
  // kicker, supporting description and a colored closing line.
  if (isCenteredCards) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-[#FAFAFA] py-20 md:py-28 px-5 md:px-8"
      >
        <div className="max-w-[1140px] mx-auto flex flex-col items-center gap-8 md:gap-10 text-center">
          {/* Header */}
          <div
            ref={headerRef}
            className="flex flex-col items-center gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {kicker && (
              <span className="rounded-full border border-[#7c5cff] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#7c5cff] md:text-[12px]">
                {kicker}
              </span>
            )}

            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-balance text-[28px] md:text-[42px] font-semibold leading-[1.2] text-[#041018]">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
          </div>

          {/* Supporting description */}
          {isFilled.richText(subtitle) && (
            <div className="w-full max-w-[900px]">
              <PrismicRichText
                field={subtitle}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-pretty text-[14px] font-normal leading-[1.6] text-[#041018]/70 md:text-[15px] [&:not(:first-child)]:mt-4">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#041018]">
                      {children}
                    </strong>
                  ),
                }}
              />
            </div>
          )}

          {/* Problem cards */}
          {cards && cards.length > 0 && (
            <div
              ref={cardsRef}
              className="grid w-full grid-cols-1 gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out md:grid-cols-3"
            >
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center gap-3 rounded-[24px] bg-white px-6 py-8 text-center shadow-[0_2px_24px_rgba(4,16,24,0.05)]"
                >
                  {card.card_title && (
                    <h3 className="text-[20px] font-bold leading-[1.3] text-[#4b0fea]">
                      {card.card_title}
                    </h3>
                  )}
                  {card.card_description && (
                    <p className="text-[14px] font-normal leading-[1.5] text-[#041018]/70">
                      {card.card_description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Colored closing line */}
          {closing && (
            <p className="max-w-[900px] text-balance text-[16px] font-bold leading-[1.4] text-[#4b0fea] md:text-[18px]">
              {closing}
            </p>
          )}
        </div>
      </section>
    );
  }

  // Render dark variations
  if (isDarkSided || isDarkNormal) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-[#041118] py-16 md:py-20 lg:py-28 px-5 md:px-16"
      >
        <div
          className={`max-w-[1040px] mx-auto flex gap-10 items-start ${
            isDarkSided ? 'flex-col lg:flex-row' : 'flex-col'
          }`}
        >
          {/* Left Column - Header */}
          <div
            ref={headerRef}
            className={`flex flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out ${
              isDarkSided ? 'flex-1' : 'w-full max-w-[760px]'
            }`}
          >
            <div
              className={`flex items-start ${
                isDarkNormal ? 'gap-3 md:gap-4' : 'flex-col gap-6'
              }`}
            >
              {/* Main Icon */}
              {isFilled.image(main_icon) && (
                <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0">
                  <PrismicNextImage
                    field={main_icon}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              <div className="flex flex-col gap-4">
                {/* Title */}
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white text-left">
                          {children}
                        </h2>
                      ),
                    }}
                  />
                )}

                {/* Subtitle */}
                {isFilled.richText(subtitle) && (
                  <PrismicRichText
                    field={subtitle}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-base md:text-lg font-normal leading-[1.5] text-white text-left">
                          {children}
                        </p>
                      ),
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Problem Cards */}
          <div
            ref={cardsRef}
            className={`opacity-0 translate-y-8 transition-all duration-700 ease-out ${
              isDarkSided
                ? 'flex-1 flex flex-col gap-2'
                : 'flex flex-wrap gap-4 w-full'
            }`}
          >
            {/* Intro text above cards */}
            {isDarkSided &&
              'side_description' in slice.primary &&
              slice.primary.side_description && (
                <p className="text-base md:text-lg font-normal leading-[1.5] text-white mb-2">
                  {slice.primary.side_description}
                </p>
              )}

            {problems && problems.length > 0 && (
              <>
                {problems.map((problem, index) => (
                  <div
                    key={index}
                    className={`flex flex-col p-4 rounded-[24px] border border-[#513434] ${
                      isDarkSided
                        ? 'gap-6'
                        : 'gap-4 w-full md:w-[calc(33.333%-11px)]'
                    }`}
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(100, 56, 67, 0.65) 0%, rgba(92, 32, 44, 0.32) 52%, rgba(0, 0, 0, 0) 100%)',
                    }}
                  >
                    {/* Icon and Title Row */}
                    <div className="flex gap-2 items-center">
                      {/* Icon */}
                      {isFilled.image(problem.icon) && (
                        <div className="w-8 h-8 flex-shrink-0">
                          <PrismicNextImage
                            field={problem.icon}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}

                      {/* Title */}
                      {problem.title && (
                        <h3 className="text-lg md:text-xl font-semibold leading-[1.3] text-[#ff6b5e]">
                          {problem.title}
                        </h3>
                      )}
                    </div>

                    {/* Statistic (optional) */}
                    {!isDarkSided && problem.statistic && (
                      <span className="text-3xl md:text-4xl font-semibold text-[#ff6b5e]">
                        {problem.statistic}
                      </span>
                    )}

                    {/* Description */}
                    {problem.description && (
                      <p className="text-base md:text-lg font-normal leading-[1.4] text-white">
                        {problem.description}
                      </p>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Default variation (headline_four_stats)
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFAFA] py-16 md:py-20 lg:py-28 px-5 md:px-16"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-10 md:gap-[60px] items-center">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col gap-4 items-center text-center max-w-[800px] opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Main Icon */}
          {isFilled.image(main_icon) && (
            <div className="w-16 h-16 md:w-[70px] md:h-[70px] flex-shrink-0 mb-2">
              <PrismicNextImage
                field={main_icon}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Title */}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {/* Subtitle */}
          {isFilled.richText(subtitle) && (
            <PrismicRichText
              field={subtitle}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-base md:text-lg font-normal leading-[1.5] text-[#041018]">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        {/* Problem Cards Grid */}
        {problems && problems.length > 0 && (
          <div
            ref={cardsRef}
            className="flex flex-wrap gap-4 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {problems.map((problem, index) => (
              <div
                key={index}
                className="flex flex-col gap-4 p-4 rounded-[24px] border border-white w-full md:w-[calc(50%-8px)]"
                style={{
                  background:
                    'linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0) 100%)',
                }}
              >
                {/* Icon and Title Row */}
                <div className="flex gap-2 items-center">
                  {/* Icon */}
                  {isFilled.image(problem.icon) && (
                    <div className="w-8 h-8 flex-shrink-0">
                      <PrismicNextImage
                        field={problem.icon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}

                  {/* Title */}
                  {problem.title && (
                    <h3 className="text-lg md:text-xl font-semibold leading-[1.4] text-[#041018]">
                      {problem.title}
                    </h3>
                  )}
                </div>

                {/* Statistic (optional) */}
                {problem.statistic && (
                  <span className="text-3xl md:text-4xl font-semibold text-[#f5821f]">
                    {problem.statistic}
                  </span>
                )}

                {/* Description */}
                {problem.description && (
                  <p className="text-base md:text-lg font-normal leading-[1.4] text-[#041018]">
                    {problem.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProblemHighlightGrid;
